import gallery from 'actions/gallery';
import { inject, observer } from 'mobx-react';
import { message } from 'components/Dialog/Toast';
import React, { useState, useEffect, useCallback, useContext } from 'react';

import { IComponentPortrait } from '../../component.d';
import { ThemeContext } from '../../store/ThemeProvider';

import './portrait.scss';

interface IPortraitProps extends IComponentPortrait {
  onComplete?: () => void;
}

// 记录肖像权授权的API调用
const submitPortraitAuth = async () => {
  const linkId = window.pageDetail?.link_id;
  const parentLinkId = window.visitParentLinkId;

  if (!linkId) {
    console.warn('No link_id found for portrait auth');
    return;
  }

  try {
    // 1. 调用 validation 接口
    gallery.validatePhoneV2(linkId, 1, 'regist').then(() => {
      // 2. 调用 paper 接口记录授权数据
      gallery.validatePostPaperV2(linkId, parentLinkId, 'regist', [
        {
          type: 'form_checkbox',
          field: '同意授权',
          required: false,
          value: ['同意'],
        },
      ]);
    });
  } catch (e) {
    console.error('Portrait auth API error:', e);
  }
};

const AllForPortrait: React.FC<IPortraitProps> = inject('rootStore')(
  observer((props) => {
    const {
      agreementContent = '<p>请阅读肖像权协议内容</p>',
      agreeButtonText = '同意并授权',
      showSuccessPage = true,
      successContent = '<p style="text-align:center;"><strong>授权成功</strong></p>',
      enterButtonText = '进入相册',
      countdown = 3,
      rootStore,
      onComplete,
    } = props;

    const themeContext = useContext(ThemeContext);
    const { mainColor } = themeContext;

    // 状态: 'agreement' | 'countdown' | 'success' | 'complete'
    // 如果 showSuccessPage 为 false，点击同意后进入 'countdown' 显示倒计时 Modal
    // 如果 showSuccessPage 为 true，点击同意后直接进入 'success' 显示成功页（完全替换协议页）
    const [step, setStep] = useState<
      'agreement' | 'countdown' | 'success' | 'complete'
    >('agreement');
    const [countdownValue, setCountdownValue] = useState(countdown);
    const [hasAgreed, setHasAgreed] = useState(false);

    // 检查是否已经授权过
    useEffect(() => {
      const linkId = window.pageDetail?.link_id;
      if (linkId && rootStore && rootStore.pagedataStore) {
        rootStore.pagedataStore.validatePassV2(linkId).then((res: any) => {
          if (res && res.code === 200) {
            const { data } = res;
            if (data && data.is_pass) {
              setHasAgreed(true);
              setStep('complete');
              console.log('completed from api validation', onComplete);
              onComplete && onComplete();
            }
          }
        });
      }
    }, [onComplete, rootStore]);

    // 倒计时逻辑（只在不显示成功页时的 countdown 状态使用）
    useEffect(() => {
      if (step === 'countdown' && countdownValue > 0) {
        const timer = setTimeout(() => {
          setCountdownValue(countdownValue - 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else if (step === 'countdown' && countdownValue === 0) {
        handleEnterGallery();
      }
    }, [step, countdownValue]);

    // 保存授权状态
    const saveAgreedStatus = useCallback(() => {
      const linkId = window.pageDetail?.link_id;
      if (linkId) {
        const storageKey = `portrait_agreed_${linkId}`;
        localStorage.setItem(storageKey, 'true');
      }
    }, []);

    // 点击同意并授权
    const handleAgree = useCallback(async () => {
      const hasIsPreview = window.location.href.includes('isPreview');
      if (hasIsPreview) {
        message('预览状态下无法完成授权操作');
        return;
      }

      // 调用API记录授权
      await submitPortraitAuth();

      saveAgreedStatus();
      setHasAgreed(true);

      if (showSuccessPage) {
        // 显示成功页（完全替换协议页，用户点击后进入相册）
        setStep('success');
      } else {
        // 不显示成功页，显示倒计时 Modal，倒计时结束后自动进入相册
        setStep('countdown');
      }
    }, [showSuccessPage, saveAgreedStatus]);

    // 进入相册
    const handleEnterGallery = useCallback(() => {
      setStep('complete');
      onComplete && onComplete();
    }, [onComplete]);

    // 如果已经完成授权，不显示任何内容
    if (step === 'complete') {
      return null;
    }

    return (
      <div className="portrait-container">
        {/* 协议页面 */}
        {step === 'agreement' && (
          <div className="portrait-agreement">
            <div
              className="portrait-content"
              dangerouslySetInnerHTML={{ __html: agreementContent }}
            />
            <div className="portrait-button-area">
              <button
                className="portrait-btn portrait-btn-primary"
                style={{ backgroundColor: mainColor }}
                onClick={handleAgree}
              >
                {agreeButtonText}
              </button>
            </div>
          </div>
        )}

        {/* 成功页（showSuccessPage=true时显示，完全替换协议页） */}
        {step === 'success' && (
          <div className="portrait-agreement portrait-success-page">
            <div className="portrait-success-icon-wrapper">
              <svg viewBox="0 0 24 24" width="64" height="64">
                <path
                  fill={mainColor}
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                />
              </svg>
            </div>
            <div
              className="portrait-content portrait-success-content-full"
              dangerouslySetInnerHTML={{ __html: successContent }}
            />
            <div className="portrait-button-area">
              <button
                className="portrait-btn portrait-btn-primary"
                style={{ backgroundColor: mainColor }}
                onClick={handleEnterGallery}
              >
                {enterButtonText}
              </button>
            </div>
          </div>
        )}

        {/* 倒计时 Modal（showSuccessPage=false时显示） */}
        {step === 'countdown' && (
          <div className="portrait-countdown-modal">
            <div className="portrait-countdown-overlay">
              <div className="portrait-countdown-content">
                <div className="portrait-countdown-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48">
                    <path
                      fill={mainColor}
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                    />
                  </svg>
                </div>
                <div className="portrait-countdown-text">授权成功</div>
                <div className="portrait-countdown-number">
                  正在进入相册（{countdownValue}S）
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })
);

export default AllForPortrait;
